import React, { useMemo } from 'react';
import { SearchFilterBar } from 'components';
import { IZone } from 'models';
import { getBuildingLocation, naturalSort } from 'utils';

type FilterKey = 'address' | 'floor' | 'name';

type Props = {
  filters: {
    search: string;
    address: string;
    floor: string;
    name: string;
  };
  zones: IZone[];
  shouldShowFilters?: boolean;
  onSearchChange: (value: string) => void;
  onFiltersChange: (updated: Partial<Omit<Props['filters'], 'search'>>) => void;
};

function buildFilterFields(zones: IZone[]) {
  const acc = zones.reduce<Record<FilterKey, Set<string>>>(
    (acc, zone) => {
      if (zone.building) {
        const location = getBuildingLocation(zone.building);
        if (location) acc.address.add(location);
      }
      if (zone.floorInfo) acc.floor.add(zone.floorInfo);
      if (zone.name) acc.name.add(zone.name);
      return acc;
    },
    { address: new Set(), floor: new Set(), name: new Set() },
  );

  return Object.entries(acc).map(([key, values]) => ({
    key: key as FilterKey,
    label: key.charAt(0).toUpperCase() + key.slice(1),
    options: Array.from(values).sort(naturalSort),
  }));
}

export function ZoneTabSearch({ filters, zones, shouldShowFilters = true, onSearchChange, onFiltersChange }: Props) {
  const filterFields = useMemo(() => buildFilterFields(zones), [zones]);

  return (
    <SearchFilterBar
      searchValue={filters.search}
      onSearchChange={onSearchChange}
      filters={filters}
      onFiltersChange={onFiltersChange}
      filterFields={filterFields}
      shouldShowFilters={shouldShowFilters}
      placeholder="Enter search keyword..."
      label="Zone Search"
    />
  );
}
